import { useFindMany } from "@gadgetinc/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { useState } from "react";
import { api } from "../../api";
import LoadingSpinner from "../LoadingSpinner";
import { SegmentFiltersTooltip } from "../SegmentFiltersTooltip";
import { Badge } from "../tailwind/badge";
import { Button } from "../tailwind/button";
import EmptyState from "../tailwind/empty-state";
import { Link } from "../tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";

export function SendingListFuture({
  campaign,
  customerId = null,
  showCancelButton = false,
  showPagination = false,
  itemsPerPage = 5,
  isMobile = false,
  onRowClick = null,
  cursor: externalCursor = null,
  setCursor: externalSetCursor = null,
  pageNumber: externalPageNumber = null,
  setPageNumber: externalSetPageNumber = null,
}) {
  const [internalCursor, setInternalCursor] = useState({ first: itemsPerPage });
  const [internalPageNumber, setInternalPageNumber] = useState(1);
  const [cancellingIds, setCancellingIds] = useState(new Set());

  const cursor = externalCursor || internalCursor;
  const setCursor = externalSetCursor || setInternalCursor;
  const pageNumber = externalPageNumber || internalPageNumber;
  const setPageNumber = externalSetPageNumber || setInternalPageNumber;

  const filter = customerId
    ? [
        { customerId: { equals: customerId } },
        { status: { equals: "registered" } },
      ]
    : [
        { status: { equals: "registered" } },
        { campaignId: { equals: campaign } },
      ];

  const queryOptions = showPagination
    ? { ...cursor, filter, live: true }
    : { filter, live: true };

  const [{ data: dataFuture, fetching: fetchingFuture }] = useFindMany(
    api.sending,
    {
      ...queryOptions,
      select: {
        id: true,
        contactList: true,
        sendingDate: true,
        status: true,
        sendingBatchId: true,
        contactListSegment: {
          externalId: true,
          type: true,
        },
        segment: {
          id: true,
          name: true,
        },
        segmentFilters: true,
        campaign: {
          id: true,
          name: true,
          automationType: true,
        },
      },
      sort: {
        sendingDate: "Ascending",
      },
    },
  );

  const getNextPage = () => {
    if (dataFuture?.endCursor) {
      setCursor({ first: itemsPerPage, after: dataFuture.endCursor });
      setPageNumber(pageNumber + 1);
    }
  };

  const getPreviousPage = () => {
    if (pageNumber > 1 && dataFuture?.startCursor) {
      setCursor({ last: itemsPerPage, before: dataFuture.startCursor });
      setPageNumber(pageNumber - 1);
    }
  };

  if (fetchingFuture) {
    return <LoadingSpinner />;
  }

  const showCampaignColumn = !!customerId;
  const showSegmentColumn = !customerId && campaign && !campaign.automationType;

  if (!dataFuture || dataFuture.length === 0) {
    return <EmptyState heading="There are no scheduled messages" />;
  }

  return (
    <>
      <Table className="mb-5">
        <TableHead>
          <TableRow>
            {showCampaignColumn && <TableHeader>Campaign name</TableHeader>}
            <TableHeader className="w-[30%]">Sending date</TableHeader>
            <TableHeader className="text-center">Recipients</TableHeader>
            {showSegmentColumn && (
              <TableHeader className="w-[60%]">Segment</TableHeader>
            )}
            {showCancelButton && !isMobile && (
              <TableHeader className="text-center">Action</TableHeader>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {dataFuture.map(
            (
              {
                id,
                contactList,
                sendingDate,
                status,
                contactListSegment,
                segment,
                segmentFilters,
                campaign: sendingCampaign,
              },
              index,
            ) => (
              <TableRow
                id={id}
                key={id}
                position={index}
                {...(!isMobile &&
                  onRowClick && {
                    onClick: () => onRowClick(sendingCampaign?.id),
                  })}
              >
                {showCampaignColumn && (
                  <TableCell className="font-bold">
                    {sendingCampaign?.name}
                  </TableCell>
                )}
                <TableCell className="w-[60%]">
                  {new Date(sendingDate).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  })}
                  {status === "stopped" && (
                    <span className="font-bold text-red-600"> (Stopped)</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {contactList.length}
                </TableCell>

                {showSegmentColumn && (
                  <TableCell className="w-[60%]">
                    {segment ? (
                      <SegmentFiltersTooltip segmentFilters={segmentFilters}>
                        <span className="cursor-help">{segment.name}</span>
                      </SegmentFiltersTooltip>
                    ) : contactListSegment?.type === "klaviyo-segment" ? (
                      <Link
                        style={{
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                        }}
                        className="hover:underline"
                        href={`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
                        target="_blank"
                      >
                        {`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                )}

                {showCancelButton && !isMobile && (
                  <TableCell className="text-center">
                    {!cancellingIds.has(id) ? (
                      <Button
                        color="secondaryButton"
                        className="min-w-[100px] h-10"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setCancellingIds((prev) => new Set(prev).add(id));
                          await api.sending.update(id, {
                            status: "canceled",
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    ) : (
                      <span className="text-gray-400">Cancelling...</span>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>

      {showPagination && (
        <div className="flex justify-start my-4">
          <Button
            color="secondaryButton"
            onClick={getPreviousPage}
            disabled={!dataFuture?.hasPreviousPage}
          >
            <ArrowLeftIcon />
            Previous
          </Button>
          <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
            {pageNumber}
          </Badge>
          <Button
            color="secondaryButton"
            onClick={getNextPage}
            disabled={!dataFuture?.hasNextPage}
          >
            Next
            <ArrowRightIcon />
          </Button>
        </div>
      )}
    </>
  );
}
